import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	CustomerInput,
	CustomerListParams,
	UpdateCustomerBody,
} from '@/types/customer'
import {
	createCustomer,
	deleteCustomer,
	getCustomerById,
	getCustomers,
	updateCustomer,
} from '../services/customers'

export const customerKeys = {
	all: ['customers'] as const,
	list: (params?: CustomerListParams) =>
		[...customerKeys.all, 'list', params] as const,
	detail: (id: string) => [...customerKeys.all, 'detail', id] as const,
}

export function useCustomers(params?: CustomerListParams) {
	return useQuery({
		queryKey: customerKeys.list(params),
		queryFn: () => getCustomers(params),
		staleTime: 2 * 60 * 1000,
	})
}

export function useCustomerById(id: string) {
	return useQuery({
		queryKey: customerKeys.detail(id),
		queryFn: () => getCustomerById(id),
		enabled: !!id,
	})
}

export function useCreateCustomer() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CustomerInput) => createCustomer(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: customerKeys.all })
		},
	})
}

export function useUpdateCustomer() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: UpdateCustomerBody }) =>
			updateCustomer(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: customerKeys.all })
		},
	})
}

export function useDeleteCustomer() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteCustomer(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: customerKeys.all })
		},
	})
}
